import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Globe,
  History,
  Mail,
  MessageCircle,
  RefreshCw,
  Search,
  Send,
  Users,
} from 'lucide-react';
import {
  getSupportCampaigns,
  getSupportChannels,
  getSupportLinks,
  getSupportVisitorIds,
  getSupportVisitors,
} from '../../Services/SupportService';
import {
  SUPPORT_CHANNEL_LABELS,
  SupportBulkOutreachResult,
  SupportChannelKey,
  SupportLink,
  SupportOutreachResult,
  SupportVisitor,
} from '../../Types/SupportType';
import { PaginationResponse } from '../../../Shared/Pagination/PaginationResponse';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import { formatDateTime } from '../../../Shared/Utils/HelperUtils';
import GrowthDateFilter from '../Growth/GrowthDateFilter';
import SupportTabNav from './SupportTabNav';
import SupportOutreachModal from './SupportOutreachModal';
import SupportBulkOutreachModal from './SupportBulkOutreachModal';

const PAGE_SIZE = 20;
const headerClass = 'px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500';
const fieldClass =
  'rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50';

function SupportChannelTab({ channel }: { channel: SupportChannelKey }) {
  const [alert, setAlert] = useState<{ variant: AlertProps['variant']; title: string; message: string } | null>(null);
  const [pageData, setPageData] = useState<PaginationResponse<SupportVisitor> | null>(null);
  const [summary, setSummary] = useState<{
    visitors: number;
    registrationStarted: number;
    registered: number;
    approved: number;
    notRegistered: number;
    conversionPct: number;
  } | null>(null);
  const [links, setLinks] = useState<SupportLink[]>([]);
  const [campaigns, setCampaigns] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [campaign, setCampaign] = useState('');
  const [notRegisteredOnly, setNotRegisteredOnly] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');
  const [target, setTarget] = useState<SupportVisitor | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  // Selected visitor ids for the bulk send/share bar. Kept in a Set so toggling rows is O(1)
  // and "select all matching" can drop in the full distinct id list without duplicates.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const showAlert = (variant: AlertProps['variant'], title: string, message: string) =>
    setAlert({ variant, title, message });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const range = {
        fromDate: appliedFrom || undefined,
        toDate: appliedTo || undefined,
      };
      const [visitorsResponse, channelsResponse, linksResponse, campaignsResponse] =
        await Promise.all([
          getSupportVisitors({
            source: channel,
            campaign: campaign || undefined,
            search: search || undefined,
            notRegisteredOnly,
            ...range,
            page,
            size: PAGE_SIZE,
          }),
          getSupportChannels({ source: channel, ...range }),
          getSupportLinks({ channel, ...range }),
          // Campaign options depend only on the channel and range, not on the selected campaign.
          // Narrowing by campaign must not empty the dropdown the admin is choosing from.
          getSupportCampaigns({ source: channel, ...range }),
        ]);

      if (visitorsResponse.success && visitorsResponse.data) {
        setPageData(visitorsResponse.data);
      }
      if (channelsResponse.success && channelsResponse.data?.[0]) {
        const c = channelsResponse.data[0];
        setSummary({
          visitors: c.visitors,
          registrationStarted: c.registrationStarted,
          registered: c.registered,
          approved: c.approved,
          notRegistered: c.notRegistered,
          conversionPct: c.conversionPct,
        });
      }
      if (linksResponse.success && Array.isArray(linksResponse.data)) {
        setLinks(linksResponse.data);
      }
      if (campaignsResponse.success && Array.isArray(campaignsResponse.data)) {
        setCampaigns(campaignsResponse.data);
      }
    } catch (err) {
      console.error('Failed to load support channel data', err);
      showAlert('error', 'Load error', 'Could not load channel analytics.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, campaign, search, notRegisteredOnly, appliedFrom, appliedTo, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Any filter change invalidates the current offset. Without this, narrowing the list while on
  // page 4 shows an empty table that looks like a bug rather than an out-of-range page.
  const resetAndFetch = useCallback(
    (mutate: () => void) => {
      mutate();
      setPage(0);
    },
    []
  );

  const visitors = pageData?.content ?? [];
  const totalElements = pageData?.totalElements ?? 0;
  const totalPages = pageData?.totalPages ?? 0;

  const toggleSelected = (visitorId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(visitorId)) {
        next.delete(visitorId);
      } else {
        next.add(visitorId);
      }
      return next;
    });
  };

  // Header checkbox reflects the current page only: a checked box means "everything on this
  // page is selected", not "everything that matches". The bulk bar offers "all matching" for the
  // full population, which keeps the two intents explicit rather than folding them into one box.
  const pageSelected = visitors.length > 0 && visitors.every((v) => selectedIds.has(v.visitorId));
  const partialSelected = !pageSelected && visitors.some((v) => selectedIds.has(v.visitorId));

  const togglePageSelected = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (pageSelected) {
        visitors.forEach((v) => next.delete(v.visitorId));
      } else {
        visitors.forEach((v) => next.add(v.visitorId));
      }
      return next;
    });
  };

  // "Select all that match" fetches every distinct visitor id for the exact filter on screen and
  // folds them into the same selection set, so the bulk bar can reach people on other pages too.
  // Distinct matters: one visitor can occupy several session rows, and mailing them once per row
  // would spam the same person.
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const handleSelectAllMatching = async () => {
    setSelectAllLoading(true);
    try {
      const response = await getSupportVisitorIds({
        source: channel,
        campaign: campaign || undefined,
        search: search || undefined,
        notRegisteredOnly,
        fromDate: appliedFrom || undefined,
        toDate: appliedTo || undefined,
      });
      if (response.success && Array.isArray(response.data)) {
        setSelectedIds(new Set(response.data));
      } else {
        showAlert('error', 'Selection failed', response.message || 'Could not load matching visitors.');
      }
    } catch {
      showAlert('error', 'Selection failed', 'Could not load matching visitors.');
    } finally {
      setSelectAllLoading(false);
    }
  };

  const openBulk = () => {
    setBulkOpen(true);
  };

  const onBulkCompleted = (result: SupportBulkOutreachResult) => {
    setBulkOpen(false);
    setSelectedIds(new Set());
    if (result.failed > 0) {
      showAlert(
        'error',
        'Bulk partly failed',
        `${result.failed} visitor${result.failed === 1 ? '' : 's'} could not be recorded.`
      );
    } else {
      showAlert(
        'success',
        result.sent > 0 ? 'Bulk sent' : 'Bulk recorded',
        `${result.total} visitor${result.total === 1 ? '' : 's'} ${result.sent > 0 ? 'emailed' : 'noted'}.`
      );
    }
  };

  const stats = useMemo(
    () => [
      { label: 'Visitors', value: summary?.visitors ?? 0, tone: 'text-slate-100' },
      {
        label: 'Not registered',
        value: summary?.notRegistered ?? 0,
        tone: 'text-amber-400',
      },
      { label: 'Started', value: summary?.registrationStarted ?? 0, tone: 'text-slate-300' },
      { label: 'Registered', value: summary?.registered ?? 0, tone: 'text-emerald-400' },
      {
        // The end of the funnel, and the reason an admin cares about any of the columns to the
        // left of it. Previously computed by the backend and then dropped on the floor, which
        // made the approved-candidate join in that aggregate query pure cost.
        label: 'Approved',
        value: summary?.approved ?? 0,
        tone: 'text-violet-400',
      },
      {
        label: 'Conversion',
        value: `${(summary?.conversionPct ?? 0).toFixed(1)}%`,
        tone: 'text-cyan-400',
      },
    ],
    [summary]
  );

  const onRecorded = (result: SupportOutreachResult) => {
    if (result.status === 'FAILED') {
      showAlert('error', 'Send failed', result.error || 'The provider rejected the message.');
    } else {
      showAlert(
        'success',
        result.status === 'SENT' ? 'Sent' : 'Recorded',
        result.reason || 'The nudge was recorded.'
      );
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <SupportTabNav />

      {alert && (
        <Alert
          variant={alert.variant}
          title={alert.title}
          message={alert.message}
          autoCloseMs={6000}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          <Users size={22} />
        </span>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">
            {SUPPORT_CHANNEL_LABELS[channel]}
          </h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Visitors who arrived on this channel, newest first
          </p>
        </div>
        <button
          onClick={fetchData}
          title="Refresh"
          className="ml-auto inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

<div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
              {stats.map((stat) => (
          <div key={stat.label} className="rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              {stat.label}
            </div>
            <div className={`mt-1 text-xl font-black tabular-nums ${stat.tone}`}>
              {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
            </div>
          </div>
        ))}
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 xl:grid-cols-2">
        <GrowthDateFilter
          fromDate={fromDate}
          toDate={toDate}
          onApply={(f, t) => {
            setFromDate(f);
            setToDate(t);
            setAppliedFrom(f);
            setAppliedTo(t);
            setPage(0);
          }}
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            resetAndFetch(() => setSearch(searchInput.trim()));
          }}
          className="flex items-center gap-2"
        >
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Visitor ID, email or campaign"
            className={`${fieldClass} w-64`}
          />
          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-950 transition hover:bg-cyan-400"
          >
            <Search size={14} />
            Search
          </button>
        </form>

        {/*
          Defaults on. The whole point of opening a channel tab is finding people who did not
          finish, so that is the default view; the toggle is there for the opposite question.
        */}
        <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2">
          <input
            type="checkbox"
            checked={notRegisteredOnly}
            onChange={(e) => resetAndFetch(() => setNotRegisteredOnly(e.target.checked))}
            className="h-4 w-4 accent-cyan-500"
          />
          <span className="text-xs font-bold text-slate-200">Not registered only</span>
        </label>

        {/*
          Built from the channel's own campaigns rather than a fixed list, so it only ever offers
          something that can actually return rows. Empty until an admin has shared a tracked link.
        */}
        {campaigns.length > 0 && (
          <select
            value={campaign}
            onChange={(e) => resetAndFetch(() => setCampaign(e.target.value))}
            className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs font-semibold text-slate-100 outline-none transition focus:border-cyan-500/50"
          >
            <option value="">All campaigns</option>
            {campaigns.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}

        {(search || campaign) && (
          <button
            onClick={() => {
              setSearchInput('');
              resetAndFetch(() => {
                setSearch('');
                setCampaign('');
              });
            }}
            className="text-xs font-bold text-slate-400 underline transition hover:text-cyan-400"
          >
            Clear
          </button>
        )}
      </div>

      {loading && visitors.length === 0 ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
          <table className="w-full min-w-[1120px] text-left">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-900/60">
                <th className="w-12 px-4 py-4">
                  <input
                    type="checkbox"
                    checked={pageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = partialSelected;
                    }}
                    onChange={togglePageSelected}
                    aria-label="Select this page"
                    className="h-4 w-4 accent-cyan-500"
                  />
                </th>
                <th className={headerClass}>Visitor</th>
                <th className={headerClass}>First seen</th>
                <th className={headerClass}>Landing page</th>
                <th className={headerClass}>Campaign</th>
                <th className={headerClass}>Engagement</th>
                <th className={headerClass}>Status</th>
                <th className={headerClass}>Last outreach</th>
                <th className={headerClass} />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {visitors.map((visitor, idx) => (
                <motion.tr
                  key={visitor.visitorId}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(idx * 0.02, 0.3) }}
                  className={`transition hover:bg-slate-900/70 ${
                    selectedIds.has(visitor.visitorId) ? 'bg-cyan-500/5' : ''
                  }`}
                >
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(visitor.visitorId)}
                      onChange={() => toggleSelected(visitor.visitorId)}
                      aria-label={`Select ${visitor.fullName || visitor.email || visitor.visitorId}`}
                      className="h-4 w-4 accent-cyan-500"
                    />
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-sm font-bold text-slate-100">
                      {visitor.fullName || visitor.email || 'Anonymous'}
                    </span>
                    <span className="mt-0.5 block font-mono text-[10px] text-slate-600">
                      {visitor.visitorId.slice(0, 16)}
                    </span>
                    {visitor.email && (
                      <span className="mt-0.5 block text-[10px] font-semibold text-slate-500">
                        {visitor.email}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-xs font-semibold text-slate-300">
                    {formatDateTime(visitor.firstSeenAt)}
                  </td>
                  <td className="max-w-xs px-5 py-4">
                    <span className="block truncate text-xs font-semibold text-slate-400">
                      {visitor.landingPage || '—'}
                    </span>
                    <span className="mt-0.5 block text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                      {visitor.medium || visitor.source}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-xs font-semibold text-slate-300">
                      {visitor.campaign || '—'}
                    </span>
                    {visitor.content && (
                      <span className="mt-0.5 block text-[10px] font-semibold text-slate-600">
                        {visitor.content}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-xs font-semibold text-slate-300">
                    <span className="block">
                      {visitor.pagesViewed ?? 1} page{(visitor.pagesViewed ?? 1) === 1 ? '' : 's'}
                    </span>
                    <span className="mt-0.5 block text-[10px] text-slate-600">
                      {visitor.sessionCount ?? 1} session
                      {(visitor.sessionCount ?? 1) === 1 ? '' : 's'}
                      {visitor.deviceCategory ? ` · ${visitor.deviceCategory}` : ''}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    {visitor.registered ? (
                      <span className="inline-flex items-center gap-1 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-bold text-emerald-400">
                        <CheckCircle2 size={12} />
                        Registered
                      </span>
                    ) : visitor.registrationStarted ? (
                      <span className="inline-flex items-center gap-1 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs font-bold text-amber-400">
                        <AlertTriangle size={12} />
                        Abandoned
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-lg border border-slate-700 bg-slate-950 px-2.5 py-1 text-xs font-bold text-slate-500">
                        <Globe size={12} />
                        Browsed
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    {visitor.lastOutreachStatus ? (
                      <LastOutreachBadge status={visitor.lastOutreachStatus} />
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-lg border border-slate-800 bg-slate-950/60 px-2.5 py-1 text-xs font-semibold text-slate-600">
                        <History size={12} />
                        Never
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button
                      onClick={() => setTarget(visitor)}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-1.5 text-xs font-black text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
                    >
                      {visitor.registered ? <Mail size={13} /> : <MessageCircle size={13} />}
                      Nudge
                    </button>
                  </td>
                </motion.tr>
              ))}
              {visitors.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-5 py-16 text-center">
                    <p className="text-sm font-bold text-slate-400">
                      {notRegisteredOnly
                        ? 'No unregistered visitors on this channel in this range.'
                        : 'No visitors on this channel in this range.'}
                    </p>
                    <p className="mt-1 text-xs font-semibold text-slate-600">
                      {notRegisteredOnly
                        ? 'Everyone who arrived here finished registering.'
                        : 'Try widening the date range or clearing the search.'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-500">
            Page {page + 1} of {totalPages} · {totalElements.toLocaleString()} visitors
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={!pageData?.hasPrevious || loading}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs font-bold text-slate-300 transition hover:text-slate-100 disabled:opacity-40"
            >
              <ChevronLeft size={14} />
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!pageData?.hasNext || loading}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs font-bold text-slate-300 transition hover:text-slate-100 disabled:opacity-40"
            >
              Next
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/*
        Sticky bulk bar. Only appears when something is selected, so the table stays clean until
        the admin is working a batch. "All matching" fetches the whole filtered population rather
        than just this page, which is what turns a row-by-row nudge into an actual bulk share.
      */}
      {selectedIds.size > 0 && (
        <div className="sticky bottom-4 z-40 mt-4">
          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-cyan-500/30 bg-slate-900/95 px-4 py-3 shadow-2xl backdrop-blur">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
              <Users size={16} />
            </span>
            <span className="text-sm font-black text-slate-100">
              {selectedIds.size.toLocaleString()} selected
            </span>
            <span className="hidden text-[10px] font-semibold text-slate-500 sm:block">
              Includes people on other pages when you use “All matching”
            </span>
            <div className="ml-auto flex flex-wrap gap-2">
              {pageData && visitors.length < (pageData.totalElements ?? 0) && (
                <button
                  onClick={handleSelectAllMatching}
                  disabled={selectAllLoading}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs font-black text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-60"
                >
                  {selectAllLoading ? (
                    <RefreshCw size={13} className="animate-spin" />
                  ) : (
                    <ClipboardList size={13} />
                  )}
                  Select all matching
                </button>
              )}
              <button
                onClick={() => setSelectedIds(new Set())}
                className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs font-bold text-slate-400 transition hover:text-rose-400"
              >
                Clear
              </button>
              <button
                onClick={openBulk}
                className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-950 transition hover:from-cyan-400 hover:to-teal-400"
              >
                <Send size={13} />
                {channel === 'email' ? 'Bulk send' : 'Bulk share'}
              </button>
            </div>
          </div>
        </div>
      )}

      <SupportOutreachModal
        channel={channel}
        visitor={target}
        links={links}
        onClose={() => setTarget(null)}
        onRecorded={onRecorded}
        onError={(title, message) => showAlert('error', title, message)}
      />

      <SupportBulkOutreachModal
        channel={channel}
        visitorIds={bulkOpen ? Array.from(selectedIds) : []}
        links={links}
        onClose={() => setBulkOpen(false)}
        onCompleted={onBulkCompleted}
        onError={(title, message) => showAlert('error', title, message)}
      />
    </div>
  );
}

/**
 * Chip for the most recent outreach status of a visitor. Honest about what each value means:
 * SENT and FAILED are transmitter outcomes, SHARED and LOGGED are record-keeping outcomes for
 * channels Nexgent cannot message on, and "Never" is the row that still needs working.
 */
function LastOutreachBadge({ status }: { status: 'LOGGED' | 'SENT' | 'FAILED' | 'SHARED' }) {
  switch (status) {
    case 'SENT':
      return (
        <span className="inline-flex items-center gap-1 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-bold text-emerald-400">
          <CheckCircle2 size={12} />
          Sent
        </span>
      );
    case 'FAILED':
      return (
        <span className="inline-flex items-center gap-1 rounded-lg border border-rose-500/30 bg-rose-500/10 px-2.5 py-1 text-xs font-bold text-rose-400">
          <AlertTriangle size={12} />
          Send failed
        </span>
      );
    case 'SHARED':
      return (
        <span className="inline-flex items-center gap-1 rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-1 text-xs font-bold text-cyan-400">
          <Send size={12} />
          Shared
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-lg border border-slate-700 bg-slate-950 px-2.5 py-1 text-xs font-bold text-slate-400">
          <ClipboardList size={12} />
          Logged
        </span>
      );
  }
}

export default SupportChannelTab;
